import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import type { BacktestSettings, BacktestSettingsPatch } from 'types/history';
import './backtestSettings.css';

export interface BacktestSettingsModalProps {
	open: boolean;
	settings: BacktestSettings | null;
	saving: boolean;
	error: string | null;
	onSave: (patch: BacktestSettingsPatch) => Promise<BacktestSettings | null>;
	onClose: () => void;
}

/** The engine's own derivation, mirrored so the form can preview "auto" before
 *  the server has been asked. Kept in step with backtest.forward_horizon. */
function derivedHorizon(entryWindow: number): number {
	return entryWindow * 5 + entryWindow + 3;
}

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

/**
 * Editor for the persisted replay rules.
 *
 * The knobs answer different questions, which the copy here has to keep apart —
 * conflating the observation horizon with a "close window" is the mistake this
 * panel exists to stop. The stop multiplier has its own trap, called out in its
 * help text: widening a stop raises the win rate almost mechanically, so the
 * figure that decides is expectancy.
 */
export function BacktestSettingsModal({ open, settings, saving, error, onSave, onClose }: BacktestSettingsModalProps) {
	const [entry, setEntry] = useState('6');
	const [timeStopOn, setTimeStopOn] = useState(false);
	const [close, setClose] = useState('20');
	const [autoHorizon, setAutoHorizon] = useState(true);
	const [horizon, setHorizon] = useState('39');
	const [chase, setChase] = useState(false);
	const [intrabar, setIntrabar] = useState(false);
	const [stopMult, setStopMult] = useState('1');
	const [dirty, setDirty] = useState(false);

	// Re-seed the form from the server whenever it opens, so a cancelled edit is
	// genuinely discarded rather than lingering until reload.
	useEffect(() => {
		if (!open || !settings) return;
		setEntry(String(settings.entry_window));
		setTimeStopOn(settings.close_window > 0);
		setClose(String(settings.close_window > 0 ? settings.close_window : 20));
		setAutoHorizon(settings.observation_horizon == null);
		setHorizon(String(settings.observation_horizon ?? settings.effective_horizon));
		setChase(settings.chase_to_resolution);
		setIntrabar(settings.intrabar_resolution);
		setStopMult(String(settings.stop_mult));
		setDirty(false);
	}, [open, settings]);

	useEffect(() => {
		if (!open) return;
		const onKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape') onClose();
		};
		document.addEventListener('keydown', onKey);
		return () => document.removeEventListener('keydown', onKey);
	}, [open, onClose]);

	const entryNum = clamp(Number(entry) || 6, 1, 200);
	const closeNum = timeStopOn ? clamp(Number(close) || 0, 1, 2000) : 0;
	const autoValue = derivedHorizon(entryNum);
	// Bounds come from the server so this form enforces exactly what the engine
	// does, rather than keeping a second copy that can drift.
	const multMin = settings?.stop_mult_min ?? 1;
	const multMax = settings?.stop_mult_max ?? 5;
	const multStep = settings?.stop_mult_step ?? 0.25;
	const multNum = clamp(Number(stopMult) || 1, multMin, multMax);

	// What the run will actually observe: the horizon, widened if a long time stop
	// would otherwise not fit after a late fill.
	const effective = useMemo(() => {
		const base = autoHorizon ? autoValue : clamp(Number(horizon) || autoValue, 1, 5000);
		return closeNum ? Math.max(base, entryNum + closeNum + 3) : base;
	}, [autoHorizon, autoValue, horizon, closeNum, entryNum]);

	const horizonWidened = !autoHorizon && effective > (Number(horizon) || 0);

	if (!open) return null;

	const mark =
		<T,>(set: (v: T) => void) =>
		(v: T) => {
			setDirty(true);
			set(v);
		};

	const submit = async (e: React.FormEvent) => {
		e.preventDefault();
		const saved = await onSave({
			entry_window: entryNum,
			close_window: closeNum,
			observation_horizon: autoHorizon ? null : clamp(Number(horizon) || autoValue, 1, 5000),
			chase_to_resolution: chase,
			intrabar_resolution: intrabar,
			stop_mult: multNum,
		});
		if (saved) onClose();
	};

	return createPortal(
		<div className='bset-overlay' onMouseDown={onClose}>
			<div className='bset-modal' onMouseDown={(e) => e.stopPropagation()}>
				<div className='bset-header'>
					<div className='bset-title'>
						<span className='bset-title-main'>Backtest settings</span>
						<span className='bset-title-sub'>replay rules · saved in the database</span>
					</div>
					<button className='bset-close' onClick={onClose} aria-label='Close'>
						✕
					</button>
				</div>

				<form className='bset-body' onSubmit={submit}>
					{/* ── Entry window ── */}
					<div className='bset-field'>
						<label className='bset-label' htmlFor='bset-entry'>
							Entry window
						</label>
						<div className='bset-row'>
							<input id='bset-entry' className='bset-input' type='number' min={1} max={200} value={entry} onChange={(e) => mark(setEntry)(e.target.value)} />
							<span className='bset-unit'>bars</span>
						</div>
						<p className='bset-help'>
							How long a limit entry has to fill. If price never reaches the zone within this many candles the proposal is <strong>NO ENTRY</strong>. This is an entry deadline only — it never closes a position.
						</p>
					</div>

					{/* ── Stop-loss distance ── */}
					<div className='bset-field'>
						<label className='bset-label' htmlFor='bset-stop-mult'>
							Stop-loss distance
						</label>
						<div className='bset-row'>
							<input id='bset-stop-mult' className='bset-input' type='number' min={multMin} max={multMax} step={multStep} value={stopMult} onChange={(e) => mark(setStopMult)(e.target.value)} />
							<span className='bset-unit'>× proposed</span>
						</div>
						<p className='bset-help'>
							Replays each stop this many times further from the entry, <strong>leaving the targets where they are</strong>. <code>1</code> is the stop the analysis actually proposed.
							{multNum !== 1 && (
								<>
									{' '}
									At <strong>{multNum}×</strong> you are risking {multNum} times as much per trade, so every winner is worth {(1 / multNum).toFixed(2)}× the R it was — <strong>watch expectancy, not win rate</strong>: a wider stop raises the win rate almost by definition.
								</>
							)}
						</p>
						<p className='bset-help'>
							Saving this does not touch existing results. The next sweep is stored alongside them, so you can flip between multipliers in <em>Viewing</em> and compare.
						</p>
					</div>

					{/* ── Close window (time stop) ── */}
					{/* <div className="bset-field">
            <label className="bset-check">
              <input
                type="checkbox"
                checked={timeStopOn}
                onChange={(e) => mark(setTimeStopOn)(e.target.checked)}
              />
              <span className="bset-label">Close window (time stop)</span>
            </label>
            <div className="bset-row">
              <input
                className="bset-input"
                type="number"
                min={1}
                max={2000}
                value={close}
                disabled={!timeStopOn}
                onChange={(e) => mark(setClose)(e.target.value)}
              />
              <span className="bset-unit">bars after fill</span>
            </div>
            <p className="bset-help">
              {timeStopOn ? (
                <>
                  A filled trade that has hit neither target nor stop after {closeNum} candles is
                  closed at that bar's close and booked <strong>TIMEOUT</strong> with a real R. A
                  target reached later does not count — it is reported as context instead.
                </>
              ) : (
                <>
                  Off: a filled trade runs until it hits a target or the stop, however long that
                  takes — the historical behaviour. Turn this on to force every trade to resolve
                  inside a fixed holding period.
                </>
              )}
            </p>
          </div> */}

					{/* ── Observation horizon ── */}
					<div className='bset-field'>
						<label className='bset-check'>
							<input type='checkbox' checked={autoHorizon} onChange={(e) => mark(setAutoHorizon)(e.target.checked)} />
							<span className='bset-label'>Observation horizon — auto</span>
						</label>
						<div className='bset-row'>
							<input className='bset-input' type='number' min={1} max={5000} value={autoHorizon ? autoValue : horizon} disabled={autoHorizon} onChange={(e) => mark(setHorizon)(e.target.value)} />
							<span className='bset-unit'>
								bars
								{autoHorizon && (
									<>
										{' '}
										· auto = {autoValue} at entry {entryNum}
									</>
								)}
							</span>
						</div>
						<p className='bset-help'>
							Forward candles to fetch, and how much data must exist before an unresolved trade counts as <strong>OPEN</strong> rather than <strong>PENDING</strong>. It does not close anything.
							{horizonWidened && (
								<>
									{' '}
									Widened to <strong>{effective}</strong> so a late fill still reaches its time stop.
								</>
							)}
						</p>
					</div>

					{/* ── Chase to resolution ── */}
					<div className='bset-field'>
						<label className='bset-check'>
							<input type='checkbox' checked={chase} onChange={(e) => mark(setChase)(e.target.checked)} />
							<span className='bset-label'>Keep going until TP or SL</span>
						</label>
						<p className='bset-help'>
							{chase ? (
								<>
									Fetches past the horizon, {' '}
									{settings?.chase_max_candles ?? 2000} candles at most, until each proposal actually hits a
									target or its stop. A trade that resolves at candle 800 is then a real{' '}
									<strong>TP</strong>/<strong>SL</strong> instead of <strong>OPEN</strong>. Only the
									unresolved trades cost extra requests, but a full re-run will be slower.
								</>
							) : (
								<>
									Off: the replay sees only the horizon above, so anything unresolved inside it is{' '}
									<strong>OPEN</strong> — which may just mean we did not look far enough.
								</>
							)}
						</p>
					</div>

					{/* ── Intra-bar resolution ── */}
					<div className='bset-field'>
						<label className='bset-check'>
							<input type='checkbox' checked={intrabar} onChange={(e) => mark(setIntrabar)(e.target.checked)} />
							<span className='bset-label'>Look inside bars that hit both</span>
						</label>
						<p className='bset-help'>
							{intrabar ? (
								<>
									When one candle&rsquo;s range covers both a target and the stop, fetches that
									candle&rsquo;s sub-candles and sees which printed <strong>first</strong>. Costs one
									extra request per ambiguous bar. Not part of the sweep key &mdash; re-running with
									this changed <strong>overwrites</strong> the stored results rather than storing a
									second answer, so note the numbers first if you want to compare.
								</>
							) : (
								<>
									Off: a candle that spans both is booked <strong>SL</strong>, because OHLC alone
									cannot order them. Safe, but on 1h/4h bars it is common enough to understate the
									win rate &mdash; those are measurement artefacts, not stop-outs.
								</>
							)}
						</p>
					</div>

					<div className='bset-summary'>
						<span>
							Runs will observe <strong>{chase ? `up to ${settings?.chase_max_candles ?? 2000}` : effective}</strong> candles · entry deadline <strong>{entryNum}</strong> ·{' '}
							{closeNum ? (
								<>
									time stop <strong>{closeNum}</strong>
								</>
							) : (
								'no time stop'
							)}
						</span>
					</div>

					<p className='bset-note'>
						Saving switches the table to these rules. Earlier runs are kept — pick them again from the <em>Viewing</em> menu. Nothing is recomputed until you press <em>Re-run all</em>.
					</p>

					{error && <div className='bset-error'>{error}</div>}

					<div className='bset-actions'>
						<button type='button' className='bset-btn' onClick={onClose} disabled={saving}>
							Cancel
						</button>
						<button type='submit' className='bset-save' disabled={saving || !dirty}>
							{saving ? 'Saving…' : 'Save settings'}
						</button>
					</div>
				</form>
			</div>
		</div>,
		document.body,
	);
}
